import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MapPinIcon,
  UserIcon,
  TruckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import googleMapsLoader from '../utils/googleMapsLoader';
import ServiceLocationSelector from '../components/ServiceLocationSelector';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';
import {
  fetchClaimantNameDuplicate,
  fetchClaimNumberDuplicate,
  showDuplicateAlert,
  requestDuplicateConfirm,
  runClaimantNameDuplicateCheck
} from '../utils/duplicateClaimChecks';
import DuplicateWarningModal from '../components/DuplicateWarningModal';
import AdjusterConfirmModal from '../components/AdjusterConfirmModal';
import { useViewExistingClaim } from '../hooks/useViewExistingClaim';
import { capitalizeName } from '../utils/nameUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Trip type options
const tripTypeOptions = [
  { value: 'one_way', label: 'One Way' },
  { value: 'round_trip_call', label: 'Round Trip (Will Call)' },
  { value: 'round_trip_wait', label: 'Round Trip (Wait and Return)' },
  { value: 'multi_leg', label: 'Multi-Leg' }
];

// Order method is not needed for customer portal - will be set to 'online' automatically

// Wait time options (for round trip wait and return)
const waitTimeOptions = [
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '45', label: '45 minutes' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1.5 hours' },
  { value: '120', label: '2 hours' },
  { value: '180', label: '3 hours' },
  { value: '240', label: '4 hours' }
];

// Appointment type options for transportation
const appointmentTypeOptions = [
  { value: 'medical_appointment', label: 'Medical Appointment' },
  { value: 'legal_appointment', label: 'Legal Appointment' },
  { value: 'court_hearing', label: 'Court Hearing' },
  { value: 'deposition', label: 'Deposition' },
  { value: 'deposition_prep', label: 'Deposition Prep' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'other', label: 'Other' }
];

// Transportation service type options
const transportationServiceTypeOptions = [
  { value: 'ambulatory', label: 'Ambulatory' },
  { value: 'wheelchair', label: 'Wheelchair' },
  { value: 'stretcher', label: 'Stretcher' },
  { value: 'bls', label: 'Basic Life Support (BLS)' },
  { value: 'als', label: 'Advanced Life Support (ALS)' }
];

const initialNewClaimState = {
  claimantId: '',
  caseType: '',
  claimNumber: '',
  dateOfInjury: '',
  ctStartDate: '',
  ctEndDate: '',
  employer: '',
  diagnosis: '',
  contactClaimsHandlerId: '',
  adjustersAssistantId: '',
  nurseCaseManagerId: '',
  fieldCaseManagerId: ''
};

const initialNewClaimantState = {
  firstName: '',
  lastName: '',
  phone: '',
  dateOfBirth: '',
  language: '',
  gender: '',
  address: '',
  address_latitude: '',
  address_longitude: ''
};

const claimCaseTypeOptions = [
  { value: 'Workers Compensation', label: 'Workers Compensation' },
  { value: 'Personal Injury', label: 'Personal Injury' },
  { value: 'Medical Malpractice', label: 'Medical Malpractice' },
  { value: 'Auto Accident', label: 'Auto Accident' },
  { value: 'Slip and Fall', label: 'Slip and Fall' },
  { value: 'Product Liability', label: 'Product Liability' },
  { value: 'Other', label: 'Other' }
];

// UI Components
const Input = ({ value, onChange, onBlur, placeholder, type = 'text', className = '', label, required = false, disabled = false, readOnly = false }) => (
  <div className={className}>
    {label && (
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
    )}
    <input
      type={type}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${disabled || readOnly ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
    />
  </div>
);

const SearchableSelect = ({ 
  label, 
  value, 
  onChange, 
  options = [], 
  placeholder = "Search...", 
  className = '', 
  required = false, 
  disabled = false,
  onClear,
  onEmptyResultsChange,
  onSearchTermChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (typeof onEmptyResultsChange === 'function') {
      const noResults = searchTerm.trim().length > 0 && filteredOptions.length === 0;
      onEmptyResultsChange(noResults, searchTerm);
    }
  }, [searchTerm, filteredOptions.length, onEmptyResultsChange]);

  // Set search term when value changes (for external updates)
  useEffect(() => {
    if (value) {
      const selectedOption = options.find(option => option.value === value);
      if (selectedOption) {
        const labelValue = selectedOption.label || '';
        setSearchTerm(labelValue);
        if (onSearchTermChange) {
          onSearchTermChange(labelValue);
        }
      }
    } else {
      setSearchTerm('');
      if (onSearchTermChange) {
        onSearchTermChange('');
      }
    }
  }, [value]);

  useEffect(() => {
    if (!value) return;
    const selectedOption = options.find(option => option.value === value);
    if (selectedOption) {
      const labelValue = selectedOption.label || '';
      setSearchTerm(labelValue);
      if (onSearchTermChange) {
        onSearchTermChange(labelValue);
      }
    }
  }, [options, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (option) => {
    if (option && option.value !== undefined) {
      onChange({ target: { value: option.value } });
      const labelValue = option.label || '';
      setSearchTerm(labelValue);
      if (onSearchTermChange) {
        onSearchTermChange(labelValue);
      }
      setIsOpen(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    if (onSearchTermChange) {
      onSearchTermChange(newValue);
    }
    setIsOpen(true);
  };

  const handleClear = () => {
    setSearchTerm('');
    onChange({ target: { value: '' } });
    if (onClear) {
      onClear();
    }
    if (onSearchTermChange) {
      onSearchTermChange('');
    }
  };

  const showClearButton = value && !disabled;

  return (
    <div className={`relative searchable-select ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${showClearButton ? 'pr-16' : 'pr-10'} ${disabled ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 mr-2"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 pointer-events-none" />
        </div>
        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {filteredOptions.map((option, index) => (
              <div
                key={index}
                onClick={() => handleSelect(option)}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Step Components
const StepIndicator = ({ currentStep, totalSteps, completedSteps, onStepClick }) => {
  const steps = [
    { number: 1, title: 'Claim Info', icon: UserIcon },
    { number: 2, title: 'Transportation Details', icon: TruckIcon },
    { number: 3, title: 'Locations', icon: MapPinIcon },
    { number: 4, title: 'Review & Submit', icon: CheckCircleIcon }
  ];

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = completedSteps.has(step.number);
          const isCurrent = currentStep === step.number;
          const isClickable = step.number <= currentStep || completedSteps.has(step.number - 1);
          
          return (
            <div key={step.number} className="flex items-center">
              <div className="flex flex-col items-center">
                <button
                  onClick={() => isClickable && onStepClick(step.number)}
                  disabled={!isClickable}
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                    ${isCompleted 
                      ? 'bg-green-600 text-white' 
                      : isCurrent 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }
                    ${isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'}
                  `}
                >
                  {isCompleted ? (
                    <CheckCircleIcon className="h-5 w-5" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </button>
                <span className={`mt-2 text-xs font-medium ${
                  isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${
                  completedSteps.has(step.number) ? 'bg-green-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Step1ClaimInfo = ({ 
  formData, 
  formOptions, 
  allClaims, 
  handleInputChange, 
  handleClaimantChange, 
  handleClaimChange, 
  clearClaimantSelection,
  onAddClaimClick,
  onAddClaimantClick
}) => {
  const availableClaims = (formData.claimantId && (formOptions.claims || []).length > 0 ? formOptions.claims : allClaims || []);
  const hasNoClaims = availableClaims.length === 0;
  const [showClaimNoResults, setShowClaimNoResults] = useState(false);
  const [showClaimantNoResults, setShowClaimantNoResults] = useState(false);
  const [claimSearchTerm, setClaimSearchTerm] = useState('');
  const [claimantSearchTerm, setClaimantSearchTerm] = useState('');

  const handleInlineAddClaim = () => {
    if (onAddClaimClick) {
      onAddClaimClick(claimSearchTerm.trim());
    }
  };

  const handleInlineAddClaimant = () => {
    if (onAddClaimantClick) {
      onAddClaimantClick(claimantSearchTerm.trim());
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Claim & Claimant</h2>
        <p className="text-gray-600">Search for a claim to auto-populate claimant information</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <SearchableSelect
            label="Select Claim"
            value={formData.claimId}
            onChange={(e) => handleClaimChange(e.target.value)}
            onClear={() => handleClaimChange('')}
            options={availableClaims.map(claim => {
              const claimantName = claim.claimant_first_name && claim.claimant_last_name 
                ? `${claim.claimant_first_name} ${claim.claimant_last_name}` 
                : claim.claimant_name || '';
              const claimLabel = claimantName 
                ? `${claimantName} - ${claim.claim_number} - ${claim.case_type}`
                : `${claim.claim_number} - ${claim.case_type}`;
              return {
                value: claim.id.toString(),
                label: claimLabel
              };
            })}
            placeholder="Search claim number (auto-populates claimant)"
            required
            onEmptyResultsChange={(noResults, term) => {
              setShowClaimNoResults(noResults);
              if (noResults) setClaimSearchTerm(term || '');
            }}
            onSearchTermChange={setClaimSearchTerm}
          />
          {showClaimNoResults && (
            <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800 mb-2">
                    No matching claims found
                  </p>
                  <p className="text-xs text-yellow-700 mb-3">
                    Add a new claim to continue with this transportation request.
                  </p>
                  <button
                    type="button"
                    onClick={handleInlineAddClaim}
                    className="px-3 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add New Claim
                  </button>
                </div>
              </div>
            </div>
          )}
          {hasNoClaims && (
            <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800 mb-2">
                    No claims found
                  </p>
                  <p className="text-xs text-yellow-700 mb-3">
                    If the claim you're looking for doesn't exist, you may need to add it first.
                  </p>
                  <button
                    type="button"
                    onClick={handleInlineAddClaim}
                    className="px-3 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add New Claim
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div>
          <SearchableSelect
            label="Select Claimant"
            value={formData.claimantId}
            onChange={(e) => handleClaimantChange(e.target.value)}
            onClear={() => handleClaimantChange('')}
            options={(formOptions.claimants || []).map(claimant => {
              const name = claimant.first_name && claimant.last_name 
                ? `${claimant.first_name} ${claimant.last_name}`
                : claimant.name || `Claimant ${claimant.id}`;
              const dob = claimant.date_of_birth 
                ? (() => {
                    const datePart = (typeof claimant.date_of_birth === 'string' ? claimant.date_of_birth : '').split('T')[0];
                    if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return '';
                    const [y, m, d] = datePart.split('-').map((n) => parseInt(n, 10));
                    return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
                  })()
                : '';
              const dobText = dob ? ` - DOB: ${dob}` : '';
              return {
                value: claimant.id.toString(),
                label: `${name}${dobText}`
              };
            })}
            placeholder="Select claimant or use claim search"
            required
            onEmptyResultsChange={(noResults, term) => {
              setShowClaimantNoResults(noResults);
              if (noResults) setClaimantSearchTerm(term || '');
            }}
            onSearchTermChange={setClaimantSearchTerm}
          />
          {showClaimantNoResults && (
            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    No matching claimants found
                  </p>
                  <p className="text-xs text-blue-700 mb-3">
                    Create a claimant to auto-fill this section.
                  </p>
                  <button
                    type="button"
                    onClick={handleInlineAddClaimant}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add New Claimant
                  </button>
                </div>
              </div>
            </div>
          )}
          {formData.claimantId && (
            <button
              type="button"
              onClick={clearClaimantSelection}
              className="mt-2 text-sm text-red-600 hover:text-red-800 flex items-center"
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              Clear selection
            </button>
          )}
        </div>
      </div>
      
      {formData.claimantId && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center mb-2">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-800">
              Claimant Information Auto-filled
            </span>
          </div>
          <p className="text-xs text-blue-600">
            The claimant's information has been automatically populated and will be used for the transportation service.
          </p>
        </div>
      )}
      
      {formData.claimId && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center mb-3">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-800">
              Claim Details
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Date of Birth</p>
              <p className="text-sm text-gray-900">
                {formData.claimantDateOfBirth 
                  ? (() => {
                      const dateStr = formData.claimantDateOfBirth;
                      let date;
                      if (dateStr.includes('-') && !dateStr.includes('T')) {
                        const [year, month, day] = dateStr.split('-').map(Number);
                        date = new Date(year, month - 1, day);
                      } else {
                        date = new Date(dateStr);
                      }
                      if (isNaN(date.getTime())) return 'Not available';
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${month}/${day}/${year}`;
                    })()
                  : 'Not available'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Date of Injury</p>
              <p className="text-sm text-gray-900">
                {formData.dateOfInjury 
                  ? (() => {
                      const dateStr = formData.dateOfInjury;
                      if (dateStr.includes('-') && !dateStr.includes('T')) {
                        const [year, month, day] = dateStr.split('-').map(Number);
                        return new Date(year, month - 1, day).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        });
                      }
                      return new Date(dateStr).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    })()
                  : 'Not available'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Employer</p>
              <p className="text-sm text-gray-900">
                {formData.employer || 'Not available'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Phone Number</p>
              <p className="text-sm text-gray-900">
                {formData.claimantPhone || 'Not available'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Step2TransportationDetails = ({ formData, formOptions, handleInputChange }) => (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Transportation Details</h2>
      <p className="text-gray-600">Set the date, time, and type of transportation service</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Input
        label="Appointment Date"
        type="date"
        value={formData.appointmentDate}
        onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
        required
      />
      
      <Input
        label="Appointment Time"
        type="time"
        value={formData.appointmentTime}
        onChange={(e) => handleInputChange('appointmentTime', e.target.value)}
        required
      />
      
      <Input
        label="Arrival Time"
        type="time"
        value={formData.arrivalTime}
        onChange={(e) => handleInputChange('arrivalTime', e.target.value)}
        placeholder="When should transportation arrive?"
        required
      />
      
      <Input
        label="Pickup Time"
        type="time"
        value={formData.pickupTime}
        onChange={(e) => handleInputChange('pickupTime', e.target.value)}
        placeholder="When should pickup occur?"
        required
      />
      
      {formData.tripType === 'round_trip_wait' && (
        <SearchableSelect
          label="Wait Time"
          value={formData.waitTime}
          onChange={(e) => handleInputChange('waitTime', e.target.value)}
          options={waitTimeOptions}
          placeholder="Expected wait time (optional)"
        />
      )}
      
      
      <SearchableSelect
        label="Trip Type"
        value={formData.tripType}
        onChange={(e) => handleInputChange('tripType', e.target.value)}
        options={tripTypeOptions}
        placeholder="Select trip type"
        required
      />
      
      <SearchableSelect
        label="Appointment Type"
        value={formData.appointmentType}
        onChange={(e) => handleInputChange('appointmentType', e.target.value)}
        options={appointmentTypeOptions}
        placeholder="Select appointment type"
        required
      />
      
      <SearchableSelect
        label="Transportation Service Type"
        value={formData.transportationServiceType}
        onChange={(e) => handleInputChange('transportationServiceType', e.target.value)}
        options={transportationServiceTypeOptions}
        placeholder="Select transportation service type"
        required
      />
    </div>

    <div>
      <Input
        label="Special Instructions"
        value={formData.specialInstructions || ''}
        onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
        placeholder="Any special instructions for the transportation service..."
      />
    </div>
  </div>
);

const Step3Locations = ({ formData, formOptions, handleInputChange, pickupAutocompleteRef, dropoffAutocompleteRef, secondDropoffAutocompleteRef, mapsInitialized, setupAddressAutocomplete, handleAddTrip, handleRemoveTrip, handleTripChange, tripAutocompleteRefs, setupTripAddressAutocomplete, handlePickupServiceLocationSelect, handleDropoffServiceLocationSelect, handleSecondDropoffServiceLocationSelect }) => (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Transportation Locations</h2>
      <p className="text-gray-600">Specify pickup and dropoff locations</p>
    </div>

    <div className="space-y-6">
      {/* Multi-trip locations */}
      {formData.transportationTrips && formData.transportationTrips.length > 0 ? (
        <>
          {formData.transportationTrips.map((trip, index) => (
            <div key={trip.id} className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-xl font-bold text-blue-900 flex items-center">
                  <div className="bg-blue-500 p-3 rounded-full mr-4">
                    <span className="text-white font-bold text-lg">{index + 1}</span>
                  </div>
                  {formData.tripType === 'round_trip_call' || formData.tripType === 'round_trip_wait' ? (
                    index === 0 ? 'Outbound Trip' : 'Return Trip'
                  ) : (
                    `Trip ${index + 1}`
                  )}
                </h4>
                {formData.tripType === 'multi_leg' && formData.transportationTrips.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTrip(trip.id)}
                    className="flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200"
                  >
                    <TrashIcon className="h-4 w-4 mr-1" />
                    Remove Trip
                  </button>
                )}
              </div>
              
              {/* Pickup Location */}
              <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-l-4 border-green-500">
                <h5 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                  <div className="bg-green-500 p-2 rounded-full mr-3">
                    <span className="text-white font-bold text-sm">P</span>
                  </div>
                  Pickup Location <span className="text-red-500 ml-2">*</span>
                </h5>
                <div className="ml-12">
                  <ServiceLocationSelector
                    selectedLocation={trip.pickupServiceLocation}
                    onLocationSelect={(location) => {
                      const updatedTrips = formData.transportationTrips.map(t => {
                        if (t.id !== trip.id) return t;
                        
                        if (location) {
                          return {
                            ...t,
                            pickupServiceLocation: location,
                            pickupLocation: {
                              ...t.pickupLocation,
                              address: location.address,
                              city: location.city || '',
                              state: location.state || '',
                              zipCode: location.zip_code || ''
                            }
                          };
                        } else {
                          return {
                            ...t,
                            pickupServiceLocation: null,
                            pickupLocation: {
                              address: '',
                              city: '',
                              state: '',
                              zipCode: '',
                              specialInstructions: ''
                            }
                          };
                        }
                      });
                      
                      // Auto-populate return trip for round trips
                      if ((formData.tripType === 'round_trip_call' || formData.tripType === 'round_trip_wait') && 
                          updatedTrips.length === 2 && location) {
                        const firstTrip = updatedTrips[0];
                        const secondTrip = updatedTrips[1];
                        
                        if (trip.id === firstTrip.id) {
                          secondTrip.dropoffServiceLocation = location;
                          secondTrip.dropoffLocation = {
                            ...secondTrip.dropoffLocation,
                            address: location.address,
                            city: location.city || '',
                            state: location.state || '',
                            zipCode: location.zip_code || ''
                          };
                        }
                      }
                      
                      handleInputChange('transportationTrips', updatedTrips);
                    }}
                    placeholder="Select a service location or add a new one"
                    showAddNew={true}
                  />
                  {trip.pickupServiceLocation && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                      <p className="text-sm text-gray-700">{trip.pickupLocation.address}</p>
                      {trip.pickupLocation.city && (
                        <p className="text-sm text-gray-600">{trip.pickupLocation.city}, {trip.pickupLocation.state} {trip.pickupLocation.zipCode}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Dropoff Location */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border-l-4 border-purple-500">
                <h5 className="text-lg font-semibold text-purple-900 mb-4 flex items-center">
                  <div className="bg-purple-500 p-2 rounded-full mr-3">
                    <span className="text-white font-bold text-sm">D</span>
                  </div>
                  Dropoff Location <span className="text-red-500 ml-2">*</span>
                </h5>
                <div className="ml-12">
                  <ServiceLocationSelector
                    selectedLocation={trip.dropoffServiceLocation}
                    onLocationSelect={(location) => {
                      const updatedTrips = formData.transportationTrips.map(t => {
                        if (t.id !== trip.id) return t;
                        
                        if (location) {
                          return {
                            ...t,
                            dropoffServiceLocation: location,
                            dropoffLocation: {
                              ...t.dropoffLocation,
                              address: location.address,
                              city: location.city || '',
                              state: location.state || '',
                              zipCode: location.zip_code || ''
                            }
                          };
                        } else {
                          return {
                            ...t,
                            dropoffServiceLocation: null,
                            dropoffLocation: {
                              address: '',
                              city: '',
                              state: '',
                              zipCode: '',
                              specialInstructions: ''
                            }
                          };
                        }
                      });
                      
                      // Auto-populate return trip for round trips
                      if ((formData.tripType === 'round_trip_call' || formData.tripType === 'round_trip_wait') && 
                          updatedTrips.length === 2 && location) {
                        const firstTrip = updatedTrips[0];
                        const secondTrip = updatedTrips[1];
                        
                        if (trip.id === firstTrip.id) {
                          secondTrip.pickupServiceLocation = location;
                          secondTrip.pickupLocation = {
                            ...secondTrip.pickupLocation,
                            address: location.address,
                            city: location.city || '',
                            state: location.state || '',
                            zipCode: location.zip_code || ''
                          };
                        }
                      }
                      
                      handleInputChange('transportationTrips', updatedTrips);
                    }}
                    placeholder="Select a service location or add a new one"
                    showAddNew={true}
                  />
                  {trip.dropoffServiceLocation && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200">
                      <p className="text-sm text-gray-700">{trip.dropoffLocation.address}</p>
                      {trip.dropoffLocation.city && (
                        <p className="text-sm text-gray-600">{trip.dropoffLocation.city}, {trip.dropoffLocation.state} {trip.dropoffLocation.zipCode}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {/* Add Trip Button */}
          {formData.tripType === 'multi_leg' && (
            <div className="text-center">
              <button
                type="button"
                onClick={handleAddTrip}
                className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm mx-auto"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Trip
              </button>
            </div>
          )}
        </>
      ) : (
        /* Legacy format for one-way trips */
        <>
          {/* Pickup Location */}
          <div className="p-4 border border-gray-200 rounded-lg bg-green-50">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Pickup Location</h3>
            </div>
            
            <ServiceLocationSelector
              selectedLocation={formData.selectedPickupServiceLocation}
              onLocationSelect={handlePickupServiceLocationSelect}
              placeholder="Select a service location or add a new one"
              showAddNew={true}
            />
            {formData.selectedPickupServiceLocation && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-green-200">
                <p className="text-sm text-gray-700">{formData.pickupAddress}</p>
                {formData.pickupCity && (
                  <p className="text-sm text-gray-600">{formData.pickupCity}, {formData.pickupState} {formData.pickupZipCode}</p>
                )}
              </div>
            )}
          </div>

          {/* Dropoff Location */}
          <div className="p-4 border border-gray-200 rounded-lg bg-blue-50">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Dropoff Location</h3>
            </div>
            
            <ServiceLocationSelector
              selectedLocation={formData.selectedDropoffServiceLocation}
              onLocationSelect={handleDropoffServiceLocationSelect}
              placeholder="Select a service location or add a new one"
              showAddNew={true}
            />
            {formData.selectedDropoffServiceLocation && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700">{formData.dropoffAddress}</p>
                {formData.dropoffCity && (
                  <p className="text-sm text-gray-600">{formData.dropoffCity}, {formData.dropoffState} {formData.dropoffZipCode}</p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  </div>
);

const Step4Review = ({ formData, formOptions }) => (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Transportation Request</h2>
      <p className="text-gray-600">Please review all details before submitting your transportation request</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Claimant Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
          Claimant Information
        </h3>
        <div className="space-y-2 text-sm">
          <p><span className="font-medium">Claimant:</span> {formData.claimantName || 'Not selected'}</p>
          <p><span className="font-medium">Claim:</span> {formData.claimNumber || 'Not selected'}</p>
        </div>
      </div>

      {/* Transportation Details */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <TruckIcon className="h-5 w-5 mr-2 text-blue-600" />
          Transportation Details
        </h3>
        <div className="space-y-2 text-sm">
          <p><span className="font-medium">Date:</span> {formData.appointmentDate}</p>
          <p><span className="font-medium">Time:</span> {formData.appointmentTime}</p>
          <p><span className="font-medium">Trip Type:</span> {tripTypeOptions.find(t => t.value === formData.tripType)?.label}</p>
          <p><span className="font-medium">Service Type:</span> {transportationServiceTypeOptions.find(t => t.value === formData.transportationServiceType)?.label || 'Not selected'}</p>
          <p><span className="font-medium">Wait Time:</span> {formData.waitTime} minutes</p>
        </div>
      </div>

      {/* Locations */}
      <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
          <MapPinIcon className="h-5 w-5 mr-2 text-blue-600" />
          Locations
        </h3>
        
        {formData.transportationTrips && formData.transportationTrips.length > 0 ? (
          /* Multi-trip format */
          <div className="space-y-4">
            {formData.transportationTrips.map((trip, index) => (
              <div key={trip.id} className="border rounded-lg p-4 bg-white">
                <h4 className="font-medium text-blue-700 mb-3">
                  {formData.tripType === 'round_trip_call' || formData.tripType === 'round_trip_wait' ? (
                    index === 0 ? 'Outbound Trip' : 'Return Trip'
                  ) : (
                    `Trip ${index + 1}`
                  )}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-green-700 mb-2">Pickup Location</h5>
                    <p className="text-sm text-gray-600">{trip.pickupLocation.address}</p>
                    <p className="text-sm text-gray-600">{trip.pickupLocation.city}, {trip.pickupLocation.state} {trip.pickupLocation.zipCode}</p>
                    {trip.pickupLocation.specialInstructions && (
                      <p className="text-sm text-gray-500 mt-1">
                        <span className="font-medium">Instructions:</span> {trip.pickupLocation.specialInstructions}
                      </p>
                    )}
                  </div>
                  <div>
                    <h5 className="font-medium text-purple-700 mb-2">Dropoff Location</h5>
                    <p className="text-sm text-gray-600">{trip.dropoffLocation.address}</p>
                    <p className="text-sm text-gray-600">{trip.dropoffLocation.city}, {trip.dropoffLocation.state} {trip.dropoffLocation.zipCode}</p>
                    {trip.dropoffLocation.specialInstructions && (
                      <p className="text-sm text-gray-500 mt-1">
                        <span className="font-medium">Instructions:</span> {trip.dropoffLocation.specialInstructions}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Legacy format for one-way trips */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-green-700 mb-2">Pickup Location</h4>
              <p className="text-sm text-gray-600">{formData.pickupAddress}</p>
              <p className="text-sm text-gray-600">{formData.pickupCity}, {formData.pickupState} {formData.pickupZipCode}</p>
            </div>
            <div>
              <h4 className="font-medium text-blue-700 mb-2">Dropoff Location</h4>
              <p className="text-sm text-gray-600">{formData.dropoffAddress}</p>
              <p className="text-sm text-gray-600">{formData.dropoffCity}, {formData.dropoffState} {formData.dropoffZipCode}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

const Step5Attachment = ({ formData, handleInputChange }) => {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file');
        return;
      }
      
      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      handleInputChange('attachment', file);
    }
  };

  const removeAttachment = () => {
    handleInputChange('attachment', null);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Optional PDF Attachment</h2>
        <p className="text-gray-600">Attach any relevant documents (optional)</p>
      </div>

      <div className="space-y-6">
        {!formData.attachment ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <div className="flex flex-col items-center">
              <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <label htmlFor="attachment" className="cursor-pointer">
                <span className="text-lg font-medium text-gray-700">Upload PDF Document</span>
                <p className="text-sm text-gray-500 mt-1">Click to select a PDF file (max 10MB)</p>
              </label>
              <input
                id="attachment"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="h-8 w-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">{formData.attachment.name}</p>
                  <p className="text-xs text-gray-500">
                    {(formData.attachment.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={removeAttachment}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        <p className="text-xs text-gray-500 text-center">
          💡 You can attach relevant documents like medical records, legal documents, or other supporting materials.
        </p>
      </div>
    </div>
  );
};

const NewTransportationJob = () => {
  const { makeAuthenticatedRequest, customer } = useAuth();
  const {
    viewClaim,
    pendingClaim,
    saveAssignment,
    skipPrompt,
    saving: savingRole
  } = useViewExistingClaim(makeAuthenticatedRequest, customer);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [isReRequest, setIsReRequest] = useState(false);
  const [originalAppointment, setOriginalAppointment] = useState(null);
  const [initialUrlParams, setInitialUrlParams] = useState({ claimantId: null, claimId: null });
  const [mapsInitialized, setMapsInitialized] = useState(false);
  const pickupAutocompleteRef = useRef(null);
  const dropoffAutocompleteRef = useRef(null);
  const secondDropoffAutocompleteRef = useRef(null);
  const claimantAddressAutocompleteRef = useRef(null);
  const newClaimantNamesRef = useRef({ firstName: '', lastName: '' });
  // Refs for multi-trip locations - we'll use dynamic refs
  const [tripAutocompleteRefs, setTripAutocompleteRefs] = useState({});
  const [formOptions, setFormOptions] = useState({
    claimants: [],
    claims: [],
    serviceTypes: []
  });
  const [allClaims, setAllClaims] = useState([]);
  const [formData, setFormData] = useState({
    // Transportation Details
    appointmentDate: '',
    appointmentTime: '',
    arrivalTime: '',
    pickupTime: '',
    waitTime: '',
    appointmentType: '',
    serviceType: '',
    tripType: '',
    specialInstructions: '',
    phone: '',
    
    // Claimant & Claim Selection
    claimantId: '',
    claimId: '',
    
    // Auto-populated Claimant Information (read-only)
    claimantName: '',
    claimNumber: '',
    claimantDateOfBirth: '',
    claimantPhone: '',
    
    // Claim Information (populated when claim is selected)
    dateOfInjury: '',
    employer: '',
    
    // Location Information (legacy format for one-way trips)
    pickupAddress: '',
    pickupCity: '',
    pickupState: '',
    pickupZipCode: '',
    dropoffAddress: '',
    dropoffCity: '',
    dropoffState: '',
    dropoffZipCode: '',
    secondDropoffAddress: '',
    secondDropoffCity: '',
    secondDropoffState: '',
    secondDropoffZipCode: '',
    
    // Service Location Information
    selectedPickupServiceLocation: null,
    selectedDropoffServiceLocation: null,
    selectedSecondDropoffServiceLocation: null,
    pickupServiceLocationId: '',
    dropoffServiceLocationId: '',
    secondDropoffServiceLocationId: '',
    
    // Multi-trip transportation support
    transportationTrips: [],
    
    // PDF Attachment
    attachment: null
  });
  const [showAddClaimModal, setShowAddClaimModal] = useState(false);
  const [isSavingClaim, setIsSavingClaim] = useState(false);
  const [newClaimData, setNewClaimData] = useState(initialNewClaimState);
  const [newClaimNumberWarning, setNewClaimNumberWarning] = useState(null);
  const [newClaimantNameWarning, setNewClaimantNameWarning] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null);
const [showAddClaimantModal, setShowAddClaimantModal] = useState(false);
const [isSavingClaimant, setIsSavingClaimant] = useState(false);
  const [newClaimantData, setNewClaimantData] = useState(initialNewClaimantState);
  const [customers, setCustomers] = useState([]);

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const totalSteps = 5;

  // Initialize Google Maps
  const initializeGoogleMaps = async () => {
    console.log('Initializing Google Maps...');
    
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      console.warn('Google Maps API key not found. Address autocomplete will be disabled.');
      return;
    }
    
    try {
      await googleMapsLoader.load(['places']);
      console.log('Google Maps API loaded successfully');
      setMapsInitialized(true);
    } catch (error) {
      console.error('Failed to load Google Maps API:', error);
    }
  };

  // Setup address autocomplete for a specific input element
  const setupAddressAutocomplete = (inputElement, locationType) => {
    console.log(`Setting up address autocomplete for ${locationType}:`, inputElement);
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps Places API not available');
      return;
    }

    // Check if autocomplete is already set up for this element
    if (inputElement.getAttribute('data-autocomplete-setup') === 'true') {
      console.log('Autocomplete already set up for this element');
      return;
    }

    console.log('Creating Google Places Autocomplete instance');
    const autocompleteInstance = new window.google.maps.places.Autocomplete(inputElement, {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    });

    // Mark element as having autocomplete set up
    inputElement.setAttribute('data-autocomplete-setup', 'true');

    autocompleteInstance.addListener('place_changed', () => {
      console.log('Place changed event triggered for', locationType);
      const place = autocompleteInstance.getPlace();
      console.log('Selected place:', place);
      console.log('Address components:', place.address_components);
      
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        // Parse address components with more comprehensive logic
        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let zipCode = '';
        
        if (place.address_components) {
          place.address_components.forEach(component => {
            const types = component.types;
            console.log('Processing component:', component.long_name, 'Types:', types);
            
            if (types.includes('street_number')) {
              streetNumber = component.long_name;
            } else if (types.includes('route')) {
              route = component.long_name;
            } else if (types.includes('locality')) {
              city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            } else if (types.includes('postal_code')) {
              zipCode = component.long_name;
            }
            // Fallback for city if locality is not available
            else if (!city && types.includes('administrative_area_level_2')) {
              city = component.long_name;
            }
            // Fallback for state if administrative_area_level_1 is not available
            else if (!state && types.includes('administrative_area_level_2')) {
              state = component.short_name;
            }
          });
        }
        
        const fullAddress = place.formatted_address || `${streetNumber} ${route}`.trim();
        
        console.log('Parsed address components:', {
          streetNumber,
          route,
          city,
          state,
          zipCode,
          fullAddress
        });
        
        // Update the appropriate form fields based on location type
        const updates = {};
        if (locationType === 'pickup') {
          updates.pickupAddress = fullAddress;
          updates.pickupCity = city;
          updates.pickupState = state;
          updates.pickupZipCode = zipCode;
          updates.pickupLatitude = lat;
          updates.pickupLongitude = lng;
        } else if (locationType === 'dropoff') {
          updates.dropoffAddress = fullAddress;
          updates.dropoffCity = city;
          updates.dropoffState = state;
          updates.dropoffZipCode = zipCode;
          updates.dropoffLatitude = lat;
          updates.dropoffLongitude = lng;
        } else if (locationType === 'secondDropoff') {
          updates.secondDropoffAddress = fullAddress;
          updates.secondDropoffCity = city;
          updates.secondDropoffState = state;
          updates.secondDropoffZipCode = zipCode;
          updates.secondDropoffLatitude = lat;
          updates.secondDropoffLongitude = lng;
        }
        
        setFormData(prev => ({
          ...prev,
          ...updates
        }));
      }
    });

    // Store the autocomplete instance for cleanup
    inputElement._autocompleteInstance = autocompleteInstance;
  };

  // Setup address autocomplete for claimant address
  const setupClaimantAddressAutocomplete = (inputElement) => {
    if (!inputElement) return;
    
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps Places API not available');
      return;
    }

    // Prevent multiple initializations
    if (claimantAddressAutocompleteRef.current) {
      return;
    }

    const autocompleteInstance = new window.google.maps.places.Autocomplete(inputElement, {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    });

    autocompleteInstance.addListener('place_changed', () => {
      const place = autocompleteInstance.getPlace();
      
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        setNewClaimantData(prev => ({
          ...prev,
          address: place.formatted_address || '',
          address_latitude: lat.toString(),
          address_longitude: lng.toString()
        }));
      }
    });

    claimantAddressAutocompleteRef.current = autocompleteInstance;
  };

  // Setup address autocomplete for multi-trip locations
  const setupTripAddressAutocomplete = (inputElement, tripId, locationType) => {
    console.log(`Setting up trip address autocomplete for ${tripId}-${locationType}:`, inputElement);
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps Places API not available');
      return;
    }

    // Check if autocomplete is already set up for this element
    if (inputElement.getAttribute('data-autocomplete-setup') === 'true') {
      console.log('Autocomplete already set up for this element');
      return;
    }

    console.log('Creating Google Places Autocomplete instance for trip');
    const autocompleteInstance = new window.google.maps.places.Autocomplete(inputElement, {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    });

    // Mark element as having autocomplete set up
    inputElement.setAttribute('data-autocomplete-setup', 'true');

    autocompleteInstance.addListener('place_changed', () => {
      console.log('Place changed event triggered for trip', tripId, locationType);
      const place = autocompleteInstance.getPlace();
      console.log('Selected place:', place);
      console.log('Address components:', place.address_components);
      
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        // Parse address components with more comprehensive logic
        let streetNumber = '';
        let route = '';
        let city = '';
        let state = '';
        let zipCode = '';
        
        if (place.address_components) {
          place.address_components.forEach(component => {
            const types = component.types;
            console.log('Processing component:', component.long_name, 'Types:', types);
            
            if (types.includes('street_number')) {
              streetNumber = component.long_name;
            } else if (types.includes('route')) {
              route = component.long_name;
            } else if (types.includes('locality')) {
              city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            } else if (types.includes('postal_code')) {
              zipCode = component.long_name;
            }
            // Fallback for city if locality is not available
            else if (!city && types.includes('administrative_area_level_2')) {
              city = component.long_name;
            }
            // Fallback for state if administrative_area_level_1 is not available
            else if (!state && types.includes('administrative_area_level_2')) {
              state = component.short_name;
            }
          });
        }
        
        const fullAddress = place.formatted_address || `${streetNumber} ${route}`.trim();
        
        console.log('Parsed address components:', {
          streetNumber,
          route,
          city,
          state,
          zipCode,
          fullAddress
        });
        
        // Update the trip data using handleTripChange - update all fields at once
        const updatedTrip = formData.transportationTrips.find(t => t.id === tripId);
        if (updatedTrip) {
          const updatedTrips = formData.transportationTrips.map(trip => 
            trip.id === tripId ? { 
              ...trip,
              [locationType]: {
                ...trip[locationType],
                address: fullAddress,
                city: city,
                state: state,
                zipCode: zipCode
              }
            } : trip
          );
          
          // Auto-populate return trip for round trips
          if ((formData.tripType === 'round_trip_call' || formData.tripType === 'round_trip_wait') && 
              updatedTrips.length === 2) {
            const firstTrip = updatedTrips[0];
            const secondTrip = updatedTrips[1];
            
            // If first trip's dropoff is selected, auto-populate second trip's pickup
            if (tripId === firstTrip.id && locationType === 'dropoffLocation') {
              secondTrip.pickupLocation.address = fullAddress;
              secondTrip.pickupLocation.city = city;
              secondTrip.pickupLocation.state = state;
              secondTrip.pickupLocation.zipCode = zipCode;
            }
            
            // If first trip's pickup is selected, auto-populate second trip's dropoff
            if (tripId === firstTrip.id && locationType === 'pickupLocation') {
              secondTrip.dropoffLocation.address = fullAddress;
              secondTrip.dropoffLocation.city = city;
              secondTrip.dropoffLocation.state = state;
              secondTrip.dropoffLocation.zipCode = zipCode;
            }
          }
          
          handleInputChange('transportationTrips', updatedTrips);
        }
      }
    });

    // Store the autocomplete instance for cleanup
    inputElement._autocompleteInstance = autocompleteInstance;
  };

  const loadFormOptions = useCallback(async () => {
    try {
      const [claimantsData, serviceTypesData, allClaimsData, customersData] = await Promise.all([
        makeAuthenticatedRequest('/claimants'),
        makeAuthenticatedRequest('/parametric/service-types'),
        makeAuthenticatedRequest('/claims'),
        makeAuthenticatedRequest('/customers')
      ]);
      
      setFormOptions({
        claimants: claimantsData.success ? claimantsData.data : [],
        serviceTypes: serviceTypesData.success ? serviceTypesData.data : []
      });
      
      // Store all claims for searchable dropdown
      setAllClaims(allClaimsData.success ? allClaimsData.data : []);
      setCustomers(customersData.success ? customersData.data : []);
    } catch (error) {
      console.error('Error loading form options:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load form options'));
    }
  }, [makeAuthenticatedRequest]);

  // Load form options on component mount
  useEffect(() => {
    loadFormOptions();
    initializeGoogleMaps();
  }, [loadFormOptions]);

  // Handle URL parameters for pre-selecting claimant and claim
  useEffect(() => {
    const claimantId = searchParams.get('claimantId');
    const claimId = searchParams.get('claimId');
    
    if (claimantId || claimId) {
      setInitialUrlParams({ claimantId, claimId });
    }
  }, [searchParams]);

  const loadClaimsForClaimant = useCallback(async (claimantId) => {
    try {
      const data = await makeAuthenticatedRequest(`${API_BASE}/customer/claimants/${claimantId}/claims`);
      
      if (data.success) {
        setFormOptions(prev => ({
          ...prev,
          claims: data.data
        }));
        return data.data;
      } else {
        console.error('Failed to load claims:', data.message);
        setFormOptions(prev => ({ ...prev, claims: [] }));
        return [];
      }
    } catch (error) {
      console.error('Error loading claims:', error);
      setFormOptions(prev => ({ ...prev, claims: [] }));
      return [];
    }
  }, [makeAuthenticatedRequest]);

  const loadOriginalAppointment = useCallback(async (appointmentId) => {
    try {
      const data = await makeAuthenticatedRequest(`/appointments/${appointmentId}`);
      
      if (data.success) {
        setOriginalAppointment(data.data);
        return data.data;
      } else {
        toast.error(data.message || 'Failed to load original appointment');
        return null;
      }
    } catch (error) {
      console.error('Error loading original appointment:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load original appointment'));
      return null;
    }
  }, [makeAuthenticatedRequest]);

  const populateFormFromOriginalAppointment = useCallback(async (originalAppointment) => {
    // Populate form data from original transportation appointment
    setFormData(prev => ({
      ...prev,
      // Don't copy date/time fields for re-request
      appointmentType: originalAppointment.appointment_type || '',
      serviceType: originalAppointment.transportation_service_type || '',
      tripType: originalAppointment.trip_type || '',
      waitTime: originalAppointment.wait_time ? originalAppointment.wait_time.toString() : '',
      specialInstructions: originalAppointment.special_instructions || '',
      phone: originalAppointment.phone || '',
      claimantId: originalAppointment.claimant_id ? originalAppointment.claimant_id.toString() : '',
    }));

    // Load claims for the claimant first
    if (originalAppointment.claimant_id) {
      await loadClaimsForClaimant(originalAppointment.claimant_id.toString());
    }

    // Then set the claim ID after claims are loaded
    if (originalAppointment.claim_id) {
      setFormData(prev => ({
        ...prev,
        claimId: originalAppointment.claim_id.toString()
      }));
    }

    // Handle location data based on format
    if (originalAppointment.transportation_locations && originalAppointment.transportation_locations.length > 0) {
      // New multi-trip format
      const locations = originalAppointment.transportation_locations.sort((a, b) => a.leg_sequence - b.leg_sequence);
      
      // Convert to multi-trip format for the form
      const formattedLocations = locations.map(loc => ({
        id: `location-${Date.now()}-${Math.random()}`,
        address: loc.address || '',
        city: loc.city || '',
        state: loc.state || '',
        zipCode: loc.zip_code || '',
        latitude: loc.latitude || '',
        longitude: loc.longitude || '',
        locationType: loc.location_type,
        legSequence: loc.leg_sequence
      }));
      
      setFormData(prev => ({
        ...prev,
        tripLocations: formattedLocations
      }));
    } else {
      // Legacy format
      if (originalAppointment.pickup_location) {
        setFormData(prev => ({
          ...prev,
          pickupAddress: originalAppointment.pickup_location.address || '',
          pickupCity: originalAppointment.pickup_location.city || '',
          pickupState: originalAppointment.pickup_location.state || '',
          pickupZipCode: originalAppointment.pickup_location.zip_code || '',
        }));
      }
      
      if (originalAppointment.dropoff_location) {
        setFormData(prev => ({
          ...prev,
          dropoffAddress: originalAppointment.dropoff_location.address || '',
          dropoffCity: originalAppointment.dropoff_location.city || '',
          dropoffState: originalAppointment.dropoff_location.state || '',
          dropoffZipCode: originalAppointment.dropoff_location.zip_code || '',
        }));
      }
      
      if (originalAppointment.second_dropoff_location) {
        setFormData(prev => ({
          ...prev,
          secondDropoffAddress: originalAppointment.second_dropoff_location.address || '',
          secondDropoffCity: originalAppointment.second_dropoff_location.city || '',
          secondDropoffState: originalAppointment.second_dropoff_location.state || '',
          secondDropoffZipCode: originalAppointment.second_dropoff_location.zip_code || '',
        }));
      }
    }
  }, [loadClaimsForClaimant]);

  // Handle re-request functionality - must be after function definitions
  useEffect(() => {
    const reRequestId = searchParams.get('reRequest');
    
    if (reRequestId) {
      setIsReRequest(true);
      loadOriginalAppointment(reRequestId).then(async (appointment) => {
        if (appointment) {
          await populateFormFromOriginalAppointment(appointment);
        }
      });
    }
  }, [searchParams, loadOriginalAppointment, populateFormFromOriginalAppointment]);

  const handleClaimantChange = useCallback((claimantId, preserveClaimId = false) => {
    return new Promise((resolve) => {
      if (!claimantId) {
        setFormData(prev => ({
          ...prev,
          claimantId: '',
          claimId: '',
          claimantName: '',
          claimNumber: '',
          claimantDateOfBirth: '',
          claimantPhone: '',
          dateOfInjury: '',
          employer: ''
        }));
        resolve();
        return;
      }

      const selectedClaimant = (formOptions.claimants || []).find(c => c.id === parseInt(claimantId));
      if (selectedClaimant) {
        setFormData(prev => ({
          ...prev,
          claimantId: claimantId,
          claimId: preserveClaimId ? prev.claimId : '',
          claimantName: selectedClaimant.first_name && selectedClaimant.last_name 
            ? `${selectedClaimant.first_name} ${selectedClaimant.last_name}`
            : selectedClaimant.name || `Claimant ${selectedClaimant.id}`,
          claimantDateOfBirth: selectedClaimant.date_of_birth || prev.claimantDateOfBirth,
          claimantPhone: selectedClaimant.phone || prev.claimantPhone
        }));
        
        loadClaimsForClaimant(claimantId).then(() => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  }, [formOptions.claimants, loadClaimsForClaimant]);

  // Handle claimant and claim selection from URL parameters
  useEffect(() => {
    if ((formOptions.claimants || []).length > 0 && initialUrlParams.claimantId) {
      handleClaimantChange(initialUrlParams.claimantId).then(() => {
        if (initialUrlParams.claimId) {
          setFormData(prev => ({ ...prev, claimId: initialUrlParams.claimId }));
        }
      });
    }
  }, [formOptions.claimants, initialUrlParams, handleClaimantChange]);

  // Smart phone population: Check for similar previous transportation requests
  const checkForSimilarTransportationRequests = useCallback(async (claimantId, claimId) => {
    if (!claimantId || !claimId) {
      console.log('Transportation phone auto-population skipped:', { claimantId, claimId });
      return;
    }
    
    console.log('Checking for similar transportation requests:', { claimantId, claimId });
    
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE}/customer/transportation-jobs?claimantId=${claimantId}&claimId=${claimId}&limit=10`);
      
      // Check if response is valid before trying to parse JSON
      if (!response || !response.ok) {
        console.log('No previous transportation jobs found or API error');
        return;
      }
      
      const data = await response.json();
      
      console.log('Transportation API response:', data);
      
      if (data.success && data.data.transportationJobs && data.data.transportationJobs.length > 0) {
        console.log('Found transportation jobs:', data.data.transportationJobs.length);
        
        // Find the most recent transportation job with a phone number
        const recentJob = data.data.transportationJobs.find(job => 
          job.phone && job.phone.trim() !== ''
        );
        
        console.log('Recent transportation job with phone:', recentJob);
        console.log('Current phone field:', formData.phone);
        
        if (recentJob && !formData.phone) {
          console.log('Auto-populating transportation phone:', recentJob.phone);
          
          // Auto-populate phone field
          setFormData(prev => ({
            ...prev,
            phone: recentJob.phone
          }));
          
          // Show a subtle notification
          toast.success(`Phone number auto-filled from previous transportation request`, {
            duration: 3000,
            icon: '🚗'
          });
        } else {
          console.log('No transportation auto-population needed:', { recentJob: !!recentJob, currentPhone: formData.phone });
        }
      } else {
        console.log('No transportation jobs found or API error');
      }
    } catch (error) {
      console.error('Error checking for similar transportation requests:', error);
      // Don't show error to user as this is a background feature
    }
  }, [makeAuthenticatedRequest, formData.phone]);

  // Trigger smart phone population when claimant and claim are selected
  useEffect(() => {
    if (formData.claimantId && formData.claimId) {
      checkForSimilarTransportationRequests(formData.claimantId, formData.claimId);
    }
  }, [formData.claimantId, formData.claimId, checkForSimilarTransportationRequests]);

  const handleInputChange = (field, value) => {
    // Initialize transportation trips when trip type changes to multi_leg
    if (field === 'tripType' && value === 'multi_leg') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        transportationTrips: [
          {
            id: generateTripId(),
            pickupLocation: {
              address: '',
              city: '',
              state: '',
              zipCode: '',
              specialInstructions: ''
            },
            dropoffLocation: {
              address: '',
              city: '',
              state: '',
              zipCode: '',
              specialInstructions: ''
            },
            pickupServiceLocation: null,
            dropoffServiceLocation: null
          }
        ]
      }));
      return; // Exit early to avoid double setting
    }
    
    // Handle round trip types - create return trip automatically
    if (field === 'tripType' && (value === 'round_trip_call' || value === 'round_trip_wait')) {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        transportationTrips: [
          {
            id: generateTripId(),
            pickupLocation: {
              address: '',
              city: '',
              state: '',
              zipCode: '',
              specialInstructions: ''
            },
            dropoffLocation: {
              address: '',
              city: '',
              state: '',
              zipCode: '',
              specialInstructions: ''
            },
            pickupServiceLocation: null,
            dropoffServiceLocation: null
          },
          {
            id: generateTripId(),
            pickupLocation: {
              address: '', // Will be auto-populated from first dropoff
              city: '',
              state: '',
              zipCode: '',
              specialInstructions: ''
            },
            dropoffLocation: {
              address: '', // Will be auto-populated from first pickup
              city: '',
              state: '',
              zipCode: '',
              specialInstructions: ''
            },
            pickupServiceLocation: null,
            dropoffServiceLocation: null
          }
        ]
      }));
      return; // Exit early to avoid double setting
    }
    
    // Clear transportation trips when trip type changes to one_way
    if (field === 'tripType' && value === 'one_way') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        transportationTrips: []
      }));
      return; // Exit early to avoid double setting
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openAddClaimModal = (prefillClaimNumber = '') => {
    setNewClaimData({
      ...initialNewClaimState,
      claimantId: formData.claimantId || '',
      claimNumber: prefillClaimNumber || '',
      contactClaimsHandlerId: customer?.id ? customer.id.toString() : ''
    });
    setShowAddClaimModal(true);
  };

  const closeAddClaimModal = () => {
    setShowAddClaimModal(false);
    setIsSavingClaim(false);
    setNewClaimData(initialNewClaimState);
    setNewClaimNumberWarning(null);
  };

  const handleNewClaimFieldChange = (field, value) => {
    setNewClaimData(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'claimNumber') setNewClaimNumberWarning(null);
  };

  const checkNewClaimNumberOnBlur = async () => {
    const num = (newClaimData.claimNumber || '').trim();
    if (!num) { setNewClaimNumberWarning(null); return; }
    const claim = await fetchClaimNumberDuplicate(makeAuthenticatedRequest, num);
    setNewClaimNumberWarning(claim);
    showDuplicateAlert(setDuplicateModal, 'claim', claim);
  };

  const checkNewClaimantNameOnBlur = async (firstName, lastName) => {
    await runClaimantNameDuplicateCheck(
      makeAuthenticatedRequest,
      setDuplicateModal,
      setNewClaimantNameWarning,
      firstName,
      lastName
    );
  };

  const handleCreateNewClaim = async (e) => {
    e.preventDefault();
    if (!newClaimData.claimNumber.trim()) {
      toast.error('Claim number is required');
      return;
    }
    if (!newClaimData.caseType) {
      toast.error('Case type is required');
      return;
    }
    if (!newClaimData.contactClaimsHandlerId || !newClaimData.adjustersAssistantId) {
      toast.error('Contact/claims handler and adjuster\'s assistant are required');
      return;
    }

    const claimantIdForClaim = newClaimData.claimantId || formData.claimantId || '';
    if (!claimantIdForClaim) {
      toast.error('Please select a claimant before creating a claim');
      return;
    }

    const payload = {
      case_type: newClaimData.caseType,
      claim_number: newClaimData.claimNumber.trim(),
      date_of_injury: newClaimData.dateOfInjury || null,
      ct_start_date: newClaimData.ctStartDate || null,
      ct_end_date: newClaimData.ctEndDate || null,
      diagnosis: newClaimData.diagnosis || null,
      employer_insured: newClaimData.employer || null,
      contact_claims_handler_id: newClaimData.contactClaimsHandlerId || null,
      adjusters_assistant_id: newClaimData.adjustersAssistantId || null,
      nurse_case_manager_id: newClaimData.nurseCaseManagerId || null,
      field_case_manager_id: newClaimData.fieldCaseManagerId || null,
      claimant_id: claimantIdForClaim ? parseInt(claimantIdForClaim, 10) : null
    };

    setIsSavingClaim(true);
    try {
      const claimDup = await fetchClaimNumberDuplicate(makeAuthenticatedRequest, newClaimData.claimNumber);
      setNewClaimNumberWarning(claimDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claim', claimDup))) {
        setIsSavingClaim(false);
        return;
      }

      const response = await makeAuthenticatedRequest('/claims', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.success || !response.data) {
        toast.error(response.message || 'Failed to create claim');
        setIsSavingClaim(false);
        return;
      }

      const createdClaim = response.data;
      toast.success('Claim created successfully');

      setAllClaims(prev => {
        const withoutDuplicate = prev.filter(claim => claim.id !== createdClaim.id);
        return [createdClaim, ...withoutDuplicate];
      });

      const createdClaimantId = createdClaim.claimant_id ? createdClaim.claimant_id.toString() : '';

      if (createdClaimantId) {
        if (formData.claimantId !== createdClaimantId) {
          await handleClaimantChange(createdClaimantId, true);
        } else {
          await loadClaimsForClaimant(createdClaimantId);
        }
      }

      const claimIdString = createdClaim.id.toString();
      selectClaimById(claimIdString, [createdClaim]);
      setFormData(prev => ({
        ...prev,
        claimId: claimIdString,
        claimantId: createdClaimantId || prev.claimantId
      }));

      closeAddClaimModal();
    } catch (error) {
      console.error('Error creating claim:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create claim'));
    } finally {
      setIsSavingClaim(false);
    }
  };

  const openAddClaimantModal = (prefillName = '') => {
    const trimmed = prefillName.trim();
    const currentName = formData.claimantName || '';
    let derivedFirstName = trimmed || currentName.split(' ')[0] || '';
    let derivedLastName = currentName.includes(' ') ? currentName.split(' ').slice(1).join(' ') : '';

    if (trimmed.includes(' ')) {
      const parts = trimmed.split(' ').filter(Boolean);
      derivedFirstName = parts.shift() || derivedFirstName;
      derivedLastName = parts.join(' ') || derivedLastName;
    } else if (trimmed && !derivedLastName) {
      derivedLastName = '';
    }

    setNewClaimantData({
      ...initialNewClaimantState,
      firstName: derivedFirstName,
      lastName: derivedLastName,
      phone: formData.claimantPhone || '',
      language: '',
      dateOfBirth: formData.claimantDateOfBirth || ''
    });
    setShowAddClaimantModal(true);
  };

  useEffect(() => {
    if (!showAddClaimantModal) return;

    const fn = (newClaimantNamesRef.current.firstName || newClaimantData.firstName || '').trim();
    const ln = (newClaimantNamesRef.current.lastName || newClaimantData.lastName || '').trim();
    if (!fn || !ln) return;

    const timer = setTimeout(() => {
      checkNewClaimantNameOnBlur(fn, ln);
    }, 500);

    return () => clearTimeout(timer);
  }, [newClaimantData.firstName, newClaimantData.lastName, showAddClaimantModal]);

  const closeAddClaimantModal = () => {
    setShowAddClaimantModal(false);
    setIsSavingClaimant(false);
    setNewClaimantData(initialNewClaimantState);
    setNewClaimantNameWarning(null);
    newClaimantNamesRef.current = { firstName: '', lastName: '' };
    // Reset autocomplete ref when modal closes
    if (claimantAddressAutocompleteRef.current) {
      claimantAddressAutocompleteRef.current = null;
    }
  };

  const handleNewClaimantFieldChange = (field, value) => {
    setNewClaimantData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'firstName' || field === 'lastName') {
        newClaimantNamesRef.current = {
          firstName: next.firstName,
          lastName: next.lastName
        };
      }
      return next;
    });
    if (field === 'firstName' || field === 'lastName') {
      setNewClaimantNameWarning(null);
    }
  };

  const handleCreateNewClaimant = async (e) => {
    e.preventDefault();
    if (!newClaimantData.firstName.trim() || !newClaimantData.lastName.trim()) {
      toast.error('First and last name are required');
      return;
    }

    const payload = {
      first_name: newClaimantData.firstName.trim(),
      last_name: newClaimantData.lastName.trim(),
      phone: newClaimantData.phone ? newClaimantData.phone.trim() : '',
      date_of_birth: newClaimantData.dateOfBirth || '',
      language: newClaimantData.language || '',
      gender: newClaimantData.gender || '',
      address: newClaimantData.address || '',
      address_latitude: newClaimantData.address_latitude || null,
      address_longitude: newClaimantData.address_longitude || null
    };

    setIsSavingClaimant(true);
    try {
      const claimantDup = await fetchClaimantNameDuplicate(
        makeAuthenticatedRequest,
        newClaimantData.firstName,
        newClaimantData.lastName
      );
      setNewClaimantNameWarning(claimantDup);
      if (!(await requestDuplicateConfirm(setDuplicateModal, 'claimant', claimantDup))) {
        setIsSavingClaimant(false);
        return;
      }

      const response = await makeAuthenticatedRequest('/claimants', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.success || !response.data) {
        toast.error(response.message || 'Failed to create claimant');
        setIsSavingClaimant(false);
        return;
      }

      const createdClaimant = response.data;
      toast.success('Claimant created successfully');
      setNewClaimantNameWarning(null);

      setFormOptions(prev => ({
        ...prev,
        claimants: [createdClaimant, ...(prev.claimants || [])]
      }));

      await handleClaimantChange(createdClaimant.id.toString());
      setFormData(prev => ({
        ...prev,
        claimantId: createdClaimant.id.toString()
      }));

      closeAddClaimantModal();
      
      // Prompt user to create a claim for the new claimant
      toast.success('Claimant created successfully. Please create a claim for this claimant.', {
        duration: 5000
      });
      
      // Open the claim creation modal
      setTimeout(() => {
        setNewClaimData(prev => ({
          ...prev,
          claimantId: createdClaimant.id.toString()
        }));
        setShowAddClaimModal(true);
      }, 500);
    } catch (error) {
      console.error('Error creating claimant:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to create claimant'));
    } finally {
      setIsSavingClaimant(false);
    }
  };

  // Helper to select a claim from a provided claims list
  const selectClaimById = (claimId, claimsList) => {
    if (!claimId) return;
    const selectedClaim = (claimsList || []).find(c => c.id === parseInt(claimId));
    if (!selectedClaim) return;

    const formatDateForInput = (dateValue) => {
      if (!dateValue) return '';
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    };

    setFormData(prev => {
      const updates = {
        claimId: claimId,
        claimNumber: selectedClaim.claim_number || '',
        dateOfInjury: formatDateForInput(selectedClaim.date_of_injury) || prev.dateOfInjury,
        employer: selectedClaim.employer || prev.employer
      };

      // Populate claimant date of birth and phone if available from claim
      if (selectedClaim.claimant_date_of_birth && !prev.claimantDateOfBirth) {
        updates.claimantDateOfBirth = formatDateForInput(selectedClaim.claimant_date_of_birth);
      }
      if (selectedClaim.claimant_phone && !prev.claimantPhone) {
        updates.claimantPhone = selectedClaim.claimant_phone;
      }

      return {
        ...prev,
        ...updates
      };
    });
  };

  const handleClaimChange = async (claimId) => {
    if (!claimId) {
      setFormData(prev => ({
        ...prev,
        claimId: '',
        claimNumber: ''
      }));
      return;
    }

    // If we don't have a claimant selected, search in allClaims
    if (!formData.claimantId) {
      const selectedClaim = (allClaims || []).find(c => c.id.toString() === claimId);
      if (selectedClaim) {
        // Set claimant and then load their claims (only if claimant_id exists)
        if (selectedClaim.claimant_id) {
          const claimantName = selectedClaim.claimant_first_name && selectedClaim.claimant_last_name 
            ? `${selectedClaim.claimant_first_name} ${selectedClaim.claimant_last_name}`
            : selectedClaim.claimant_name;
          
          // First, set the claim in the form data
          selectClaimById(claimId, [selectedClaim]);
          
          toast.success(`Claimant auto-populated: ${claimantName}`, {
            duration: 3000
          });
          
          // Then auto-populate the claimant, preserving the claim selection
          await handleClaimantChange(selectedClaim.claimant_id.toString(), true);
        } else {
          // Claim is missing claimant_id link, but still populate the claim
          console.warn('Selected claim has no claimant_id:', selectedClaim);
          toast.error('Please manually select the claimant for this claim', {
            duration: 4000
          });
          
          // Still populate the claim information
          selectClaimById(claimId, [selectedClaim]);
        }
      }
    } else {
      // Use current state claims list for claimant-specific claims
      selectClaimById(claimId, formOptions.claims || []);
    }
  };

  const clearClaimantSelection = () => {
    setFormData(prev => ({
      ...prev,
      claimantId: '',
      claimId: '',
      claimantName: '',
      claimNumber: '',
      claimantDateOfBirth: '',
      claimantPhone: '',
      dateOfInjury: '',
      employer: ''
    }));
    setFormOptions(prev => ({ ...prev, claims: [] }));
  };

  // Helper functions for multi-trip functionality
  const generateTripId = () => `trip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleAddTrip = () => {
    const currentTrips = formData.transportationTrips || [];
    const lastTrip = currentTrips[currentTrips.length - 1];
    
    const newTrip = {
      id: generateTripId(),
      pickupLocation: {
        address: lastTrip?.dropoffLocation?.address || '', // Auto-populate from previous dropoff
        city: lastTrip?.dropoffLocation?.city || '',
        state: lastTrip?.dropoffLocation?.state || '',
        zipCode: lastTrip?.dropoffLocation?.zipCode || '',
        specialInstructions: ''
      },
      dropoffLocation: {
        address: '',
        city: '',
        state: '',
        zipCode: '',
        specialInstructions: ''
      },
      pickupServiceLocation: null,
      dropoffServiceLocation: null
    };
    
    handleInputChange('transportationTrips', [...currentTrips, newTrip]);
  };

  const handleRemoveTrip = (tripId) => {
    const updatedTrips = formData.transportationTrips.filter(trip => trip.id !== tripId);
    handleInputChange('transportationTrips', updatedTrips);
  };

  const handleTripChange = (tripId, locationType, field, value) => {
    const updatedTrips = formData.transportationTrips.map(trip => {
      if (trip.id !== tripId) return trip;
      
      // Handle service location fields (pickupServiceLocation, dropoffServiceLocation)
      if (locationType === 'pickupServiceLocation' || locationType === 'dropoffServiceLocation') {
        return {
          ...trip,
          [locationType]: value
        };
      }
      
      // Handle nested location fields (pickupLocation.address, dropoffLocation.city, etc.)
      return {
        ...trip,
        [locationType]: {
          ...trip[locationType],
          [field]: value
        }
      };
    });
    
    // Auto-populate return trip for round trips
    if ((formData.tripType === 'round_trip_call' || formData.tripType === 'round_trip_wait') && 
        updatedTrips.length === 2) {
      const firstTrip = updatedTrips[0];
      const secondTrip = updatedTrips[1];
      
      // If first trip's dropoff location is selected, auto-populate second trip's pickup with the same location
      if (tripId === firstTrip.id && locationType === 'dropoffServiceLocation' && field === null) {
        // Copy the entire dropoff service location to return trip's pickup service location
        secondTrip.pickupServiceLocation = value;
        if (value) {
          secondTrip.pickupLocation = {
            ...secondTrip.pickupLocation,
            address: value.address || firstTrip.dropoffLocation.address,
            city: value.city || firstTrip.dropoffLocation.city,
            state: value.state || firstTrip.dropoffLocation.state,
            zipCode: value.zip_code || firstTrip.dropoffLocation.zipCode
          };
        }
      }
      
      // If first trip's dropoff address is manually entered, copy to return trip pickup
      if (tripId === firstTrip.id && locationType === 'dropoffLocation') {
        secondTrip.pickupLocation[field] = value;
      }
      
      // If first trip's pickup location is selected, auto-populate second trip's dropoff with the same location
      if (tripId === firstTrip.id && locationType === 'pickupServiceLocation' && field === null) {
        // Copy the entire pickup service location to return trip's dropoff service location
        secondTrip.dropoffServiceLocation = value;
        if (value) {
          secondTrip.dropoffLocation = {
            ...secondTrip.dropoffLocation,
            address: value.address || firstTrip.pickupLocation.address,
            city: value.city || firstTrip.pickupLocation.city,
            state: value.state || firstTrip.pickupLocation.state,
            zipCode: value.zip_code || firstTrip.pickupLocation.zipCode
          };
        }
      }
      
      // If first trip's pickup address is manually entered, copy to return trip dropoff
      if (tripId === firstTrip.id && locationType === 'pickupLocation') {
        secondTrip.dropoffLocation[field] = value;
      }
    }
    
    handleInputChange('transportationTrips', updatedTrips);
  };

  // Service location selection handlers
  const handlePickupServiceLocationSelect = (location) => {
    if (location) {
      setFormData(prev => ({
        ...prev,
        selectedPickupServiceLocation: location,
        pickupServiceLocationId: location.id,
        pickupAddress: location.address,
        pickupCity: location.city || '',
        pickupState: location.state || '',
        pickupZipCode: location.zip_code || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedPickupServiceLocation: null,
        pickupServiceLocationId: '',
        pickupAddress: '',
        pickupCity: '',
        pickupState: '',
        pickupZipCode: ''
      }));
    }
  };

  const handleDropoffServiceLocationSelect = (location) => {
    if (location) {
      setFormData(prev => ({
        ...prev,
        selectedDropoffServiceLocation: location,
        dropoffServiceLocationId: location.id,
        dropoffAddress: location.address,
        dropoffCity: location.city || '',
        dropoffState: location.state || '',
        dropoffZipCode: location.zip_code || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedDropoffServiceLocation: null,
        dropoffServiceLocationId: '',
        dropoffAddress: '',
        dropoffCity: '',
        dropoffState: '',
        dropoffZipCode: ''
      }));
    }
  };

  const handleSecondDropoffServiceLocationSelect = (location) => {
    if (location) {
      setFormData(prev => ({
        ...prev,
        selectedSecondDropoffServiceLocation: location,
        secondDropoffServiceLocationId: location.id,
        secondDropoffAddress: location.address,
        secondDropoffCity: location.city || '',
        secondDropoffState: location.state || '',
        secondDropoffZipCode: location.zip_code || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedSecondDropoffServiceLocation: null,
        secondDropoffServiceLocationId: '',
        secondDropoffAddress: '',
        secondDropoffCity: '',
        secondDropoffState: '',
        secondDropoffZipCode: ''
      }));
    }
  };

  // Step validation functions
  const validateStep = (step) => {
    switch (step) {
      case 1: // Claim Info
        return formData.claimantId && formData.claimId;
      case 2: // Transportation Details
        const basicValidation = formData.appointmentDate && formData.appointmentTime && 
               formData.arrivalTime && formData.pickupTime &&
               formData.tripType && formData.appointmentType && formData.transportationServiceType;
        
        return basicValidation;
      case 3: // Locations
        // Check if using multi-trip format
        if (formData.transportationTrips && formData.transportationTrips.length > 0) {
          // Validate each trip has both pickup and dropoff locations
          return formData.transportationTrips.every(trip => 
            trip.pickupLocation.address && trip.pickupLocation.city && trip.pickupLocation.state && trip.pickupLocation.zipCode &&
            trip.dropoffLocation.address && trip.dropoffLocation.city && trip.dropoffLocation.state && trip.dropoffLocation.zipCode
          );
        } else {
          // Legacy format validation
          return formData.pickupAddress && formData.pickupCity && formData.pickupState && formData.pickupZipCode &&
                 formData.dropoffAddress && formData.dropoffCity && formData.dropoffState && formData.dropoffZipCode;
        }
      case 4: // Review
        return true; // Review step is always valid
      case 5: // PDF Attachment (optional)
        return true; // Always valid since attachment is optional
      default:
        return false;
    }
  };

  const markStepCompleted = (step) => {
    setCompletedSteps(prev => new Set([...prev, step]));
  };

  const goToNextStep = () => {
    if (validateStep(currentStep)) {
      markStepCompleted(currentStep);
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toast.error('Please fill in all required fields before proceeding');
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    // Allow going to any completed step or the next step
    if (step <= currentStep || completedSteps.has(step - 1)) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      const basicValidation = formData.appointmentDate && formData.appointmentTime && 
                             formData.tripType && formData.appointmentType && 
                             formData.transportationServiceType && formData.claimantId && formData.claimId;
      
      if (!basicValidation) {
        toast.error('Please fill in all required fields');
        return;
      }

      const transportationData = {
        appointmentDate: formData.appointmentDate,
        appointmentTime: formData.appointmentTime,
        arrivalTime: formData.arrivalTime,
        pickupTime: formData.pickupTime,
        orderMethod: 'online', // Always set to 'online' for customer portal requests
        appointmentType: formData.appointmentType,
        transportationServiceType: formData.transportationServiceType,
        tripType: formData.tripType,
        specialInstructions: formData.specialInstructions,
        claimantId: parseInt(formData.claimantId),
        claimId: parseInt(formData.claimId),
        
        // Multi-trip transportation support
        transportationTrips: formData.transportationTrips.map(trip => ({
          ...trip,
          pickupServiceLocationId: trip.pickupServiceLocation?.id || null,
          dropoffServiceLocationId: trip.dropoffServiceLocation?.id || null,
          // Also include service_location_id in location objects for backend compatibility
          pickupLocation: trip.pickupLocation ? {
            ...trip.pickupLocation,
            serviceLocationId: trip.pickupServiceLocation?.id || null
          } : null,
          dropoffLocation: trip.dropoffLocation ? {
            ...trip.dropoffLocation,
            serviceLocationId: trip.dropoffServiceLocation?.id || null
          } : null
        }))
      };

      // Only include waitTime if it's provided and not empty
      if (formData.waitTime && formData.waitTime.trim() !== '') {
        transportationData.waitTime = parseInt(formData.waitTime);
      }

      // Add legacy location data for one-way trips (backward compatibility)
      if (formData.tripType === 'one_way') {
        transportationData.pickupLocation = {
          address: formData.pickupAddress,
          city: formData.pickupCity,
          state: formData.pickupState,
          zipCode: formData.pickupZipCode,
          latitude: formData.pickupLatitude,
          longitude: formData.pickupLongitude
        };
        transportationData.dropoffLocation = {
          address: formData.dropoffAddress,
          city: formData.dropoffCity,
          state: formData.dropoffState,
          zipCode: formData.dropoffZipCode,
          latitude: formData.dropoffLatitude,
          longitude: formData.dropoffLongitude
        };
        
        // Add service location data
        if (formData.pickupServiceLocationId) {
          transportationData.pickupServiceLocationId = formData.pickupServiceLocationId;
        }
        if (formData.dropoffServiceLocationId) {
          transportationData.dropoffServiceLocationId = formData.dropoffServiceLocationId;
        }
        if (formData.secondDropoffServiceLocationId) {
          transportationData.secondDropoffServiceLocationId = formData.secondDropoffServiceLocationId;
        }
      }

      // Create FormData for file upload support
      const formDataToSend = new FormData();
      
      // Add all transportation data to FormData
      Object.keys(transportationData).forEach(key => {
        if (key === 'transportationTrips') {
          // Handle transportation trips array
          formDataToSend.append('transportationTrips', JSON.stringify(transportationData[key]));
        } else if (key === 'pickupLocation' || key === 'dropoffLocation' || key === 'secondDropoffLocation') {
          // Handle location objects
          formDataToSend.append(key, JSON.stringify(transportationData[key]));
        } else {
          formDataToSend.append(key, transportationData[key]);
        }
      });
      
      // Add PDF attachment if provided
      if (formData.attachment) {
        formDataToSend.append('attachment', formData.attachment);
      }

      const data = await makeAuthenticatedRequest(`${API_BASE}/customer/transportation-jobs`, {
        method: 'POST',
        // Don't set Content-Type header - let the browser set it with boundary for FormData
        body: formDataToSend
      });

      if (data.success) {
        toast.success('Transportation request submitted successfully!');
        // Add a small delay to ensure the job is fully created before navigation
        setTimeout(() => {
          navigate(`/appointments/${data.data.id}`);
        }, 1000);
      } else {
        toast.error(data.message || 'Failed to submit transportation request');
      }
    } catch (error) {
      console.error('Error submitting transportation request:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to submit transportation request'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center">
            <button
              onClick={() => navigate('/appointments')}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <TruckIcon className="h-8 w-8 mr-3 text-blue-600" />
                {isReRequest ? 'Re-request Transportation Service' : 'Request Transportation Service'}
              </h1>
              <p className="mt-2 text-gray-600">
                {isReRequest 
                  ? 'Create a new transportation request with the same details as the original'
                  : 'Request transportation services for your claimant'
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Multi-Step Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow"
        >
          {isReRequest && originalAppointment && (
            <div className="p-6 border-b border-gray-200 bg-green-50">
              <div className="flex items-center">
                <InformationCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">
                    Re-requesting transportation from {originalAppointment.scheduled_date ? (() => {
                      const dateStr = originalAppointment.scheduled_date;
                      if (dateStr.includes('-') && !dateStr.includes('T')) {
                        const [year, month, day] = dateStr.split('-').map(Number);
                        return new Date(year, month - 1, day).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        });
                      }
                      return new Date(dateStr).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      });
                    })() : 'previous appointment'}
                  </h3>
                  <p className="text-sm text-green-600 mt-1">
                    The form has been pre-filled with the original transportation details. Please update the date and time for the new service.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Step Indicator */}
          <div className="p-6 border-b border-gray-200">
            <StepIndicator 
              currentStep={currentStep}
              totalSteps={totalSteps}
              completedSteps={completedSteps}
              onStepClick={goToStep}
            />
          </div>

          {/* Step Content */}
          <div className="p-6">
            <form onSubmit={handleSubmit}>
              {currentStep === 1 && (
                <Step1ClaimInfo
                  formData={formData}
                  formOptions={formOptions}
                  allClaims={allClaims}
                  handleInputChange={handleInputChange}
                  handleClaimantChange={handleClaimantChange}
                  handleClaimChange={handleClaimChange}
                  clearClaimantSelection={clearClaimantSelection}
                  onAddClaimClick={openAddClaimModal}
                  onAddClaimantClick={openAddClaimantModal}
                />
              )}
              
              {currentStep === 2 && (
                <Step2TransportationDetails
                  formData={formData}
                  formOptions={formOptions}
                  handleInputChange={handleInputChange}
                />
              )}
              
              {currentStep === 3 && (
                <Step3Locations
                  formData={formData}
                  formOptions={formOptions}
                  handleInputChange={handleInputChange}
                  pickupAutocompleteRef={pickupAutocompleteRef}
                  dropoffAutocompleteRef={dropoffAutocompleteRef}
                  secondDropoffAutocompleteRef={secondDropoffAutocompleteRef}
                  mapsInitialized={mapsInitialized}
                  setupAddressAutocomplete={setupAddressAutocomplete}
                  handleAddTrip={handleAddTrip}
                  handleRemoveTrip={handleRemoveTrip}
                  handleTripChange={handleTripChange}
                  tripAutocompleteRefs={tripAutocompleteRefs}
                  setupTripAddressAutocomplete={setupTripAddressAutocomplete}
                  handlePickupServiceLocationSelect={handlePickupServiceLocationSelect}
                  handleDropoffServiceLocationSelect={handleDropoffServiceLocationSelect}
                  handleSecondDropoffServiceLocationSelect={handleSecondDropoffServiceLocationSelect}
                />
              )}
              
              {currentStep === 4 && (
                <Step4Review
                  formData={formData}
                  formOptions={formOptions}
                />
              )}
              
              {currentStep === 5 && (
                <Step5Attachment
                  formData={formData}
                  handleInputChange={handleInputChange}
                />
              )}
            </form>
          </div>

          {/* Claimant Creation Modal */}
          {showAddClaimantModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
              <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
                <div className="flex items-center justify-between border-b pb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Add New Claimant</h3>
                    <p className="text-sm text-gray-500">
                      Create a claimant without leaving this transportation request.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeAddClaimantModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form className="mt-4 space-y-4" onSubmit={handleCreateNewClaimant}>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="First Name"
                      value={newClaimantData.firstName}
                      onChange={(e) => handleNewClaimantFieldChange('firstName', e.target.value)}
                      onBlur={(e) => {
                        const normalized = capitalizeName(e.target.value);
                        newClaimantNamesRef.current.firstName = normalized;
                        handleNewClaimantFieldChange('firstName', normalized);
                        checkNewClaimantNameOnBlur(normalized, newClaimantNamesRef.current.lastName);
                      }}
                      required
                    />
                    <Input
                      label="Last Name"
                      value={newClaimantData.lastName}
                      onChange={(e) => handleNewClaimantFieldChange('lastName', e.target.value)}
                      onBlur={(e) => {
                        const normalized = capitalizeName(e.target.value);
                        newClaimantNamesRef.current.lastName = normalized;
                        handleNewClaimantFieldChange('lastName', normalized);
                        checkNewClaimantNameOnBlur(newClaimantNamesRef.current.firstName, normalized);
                      }}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="Phone"
                      type="tel"
                      value={newClaimantData.phone}
                      onChange={(e) => handleNewClaimantFieldChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                    <Input
                      label="Date of Birth"
                      type="date"
                      value={newClaimantData.dateOfBirth}
                      onChange={(e) => handleNewClaimantFieldChange('dateOfBirth', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="Preferred Language"
                      value={newClaimantData.language}
                      onChange={(e) => handleNewClaimantFieldChange('language', e.target.value)}
                      placeholder="Optional"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Gender
                      </label>
                      <select
                        value={newClaimantData.gender}
                        onChange={(e) => handleNewClaimantFieldChange('gender', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      >
                        <option value="">Prefer not to say</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Address (optional)
                    </label>
                    <input
                      type="text"
                      value={newClaimantData.address}
                      onChange={(e) => handleNewClaimantFieldChange('address', e.target.value)}
                      placeholder={mapsInitialized ? "Start typing address for autocomplete..." : "Enter address manually"}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      ref={(input) => {
                        if (input && mapsInitialized && !claimantAddressAutocompleteRef.current) {
                          setupClaimantAddressAutocomplete(input);
                        }
                      }}
                    />
                    {(newClaimantData.address_latitude && newClaimantData.address_longitude) && (
                      <div className="mt-2 text-xs text-gray-500">
                        Coordinates: {newClaimantData.address_latitude}, {newClaimantData.address_longitude}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 border-t pt-4 text-sm text-gray-500 md:flex-row md:items-center md:justify-between">
                    <p>Claimants can be edited later from the Claims area.</p>
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeAddClaimantModal}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        disabled={isSavingClaimant}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={isSavingClaimant}
                      >
                        {isSavingClaimant && (
                          <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            />
                          </svg>
                        )}
                        Create Claimant
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Claim Creation Modal */}
          {showAddClaimModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
              <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
                <div className="flex items-center justify-between border-b pb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Add New Claim</h3>
                    <p className="text-sm text-gray-500">
                      Create a claim without leaving your transportation request.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeAddClaimModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form className="mt-4 space-y-4" onSubmit={handleCreateNewClaim}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Claimant <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newClaimData.claimantId || formData.claimantId}
                      onChange={(e) => handleNewClaimFieldChange('claimantId', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      required
                    >
                      <option value="">Select claimant...</option>
                      {(formOptions.claimants || []).map((claimant) => {
                        const name = claimant.first_name && claimant.last_name
                          ? `${claimant.first_name} ${claimant.last_name}`
                          : claimant.name || `Claimant ${claimant.id}`;
                        const dob = claimant.date_of_birth 
                          ? (() => {
                              const datePart = (typeof claimant.date_of_birth === 'string' ? claimant.date_of_birth : '').split('T')[0];
                              if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return '';
                              const [y, m, d] = datePart.split('-').map((n) => parseInt(n, 10));
                              return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
                            })()
                          : '';
                        const dobText = dob ? ` - DOB: ${dob}` : '';
                        return (
                          <option key={claimant.id} value={claimant.id}>
                            {`${name}${dobText}`}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Case Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newClaimData.caseType}
                        onChange={(e) => handleNewClaimFieldChange('caseType', e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        required
                      >
                        <option value="">Select case type...</option>
                        {claimCaseTypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Input
                        label="Claim Number"
                        value={newClaimData.claimNumber}
                        onChange={(e) => handleNewClaimFieldChange('claimNumber', e.target.value)}
                        onBlur={checkNewClaimNumberOnBlur}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="Date of Injury"
                      type="date"
                      value={newClaimData.dateOfInjury}
                      onChange={(e) => handleNewClaimFieldChange('dateOfInjury', e.target.value)}
                    />
                    <Input
                      label="Employer / Insured"
                      value={newClaimData.employer}
                      onChange={(e) => handleNewClaimFieldChange('employer', e.target.value)}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label="CT Start Date"
                      type="date"
                      value={newClaimData.ctStartDate}
                      onChange={(e) => handleNewClaimFieldChange('ctStartDate', e.target.value)}
                    />
                    <Input
                      label="CT End Date"
                      type="date"
                      value={newClaimData.ctEndDate}
                      onChange={(e) => handleNewClaimFieldChange('ctEndDate', e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Diagnosis / Notes
                    </label>
                    <textarea
                      value={newClaimData.diagnosis}
                      onChange={(e) => handleNewClaimFieldChange('diagnosis', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder="Optional context for this claim"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SearchableSelect
                      label="Contact/Claims Handler"
                      value={newClaimData.contactClaimsHandlerId}
                      onChange={(e) => handleNewClaimFieldChange('contactClaimsHandlerId', e.target.value)}
                      options={customers.map(c => ({
                        value: c.id.toString(),
                        label: c.name || 'Unnamed Customer'
                      }))}
                      placeholder="Select contact/claims handler..."
                      required
                    />
                    <SearchableSelect
                      label="Adjuster's Assistant"
                      value={newClaimData.adjustersAssistantId}
                      onChange={(e) => handleNewClaimFieldChange('adjustersAssistantId', e.target.value)}
                      options={customers.map(c => ({
                        value: c.id.toString(),
                        label: c.name || 'Unnamed Customer'
                      }))}
                      placeholder="Select adjuster's assistant..."
                      required
                    />
                    <SearchableSelect
                      label="Nurse Case Manager"
                      value={newClaimData.nurseCaseManagerId}
                      onChange={(e) => handleNewClaimFieldChange('nurseCaseManagerId', e.target.value)}
                      options={customers.map(c => ({
                        value: c.id.toString(),
                        label: c.name || 'Unnamed Customer'
                      }))}
                      placeholder="Select nurse case manager..."
                    />
                    <SearchableSelect
                      label="Field Case Manager"
                      value={newClaimData.fieldCaseManagerId}
                      onChange={(e) => handleNewClaimFieldChange('fieldCaseManagerId', e.target.value)}
                      options={customers.map(c => ({
                        value: c.id.toString(),
                        label: c.name || 'Unnamed Customer'
                      }))}
                      placeholder="Select field case manager..."
                    />
                  </div>

                  <div className="flex flex-col gap-2 border-t pt-4 text-sm text-gray-500 md:flex-row md:items-center md:justify-between">
                    <p>Claim details can also be edited later from the Claims area.</p>
                    <div className="flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={closeAddClaimModal}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        disabled={isSavingClaim}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                        disabled={isSavingClaim}
                      >
                        {isSavingClaim && (
                          <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            />
                          </svg>
                        )}
                        Create Claim
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => navigate('/appointments')}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            
            <div className="flex space-x-4">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={goToPreviousStep}
                  disabled={loading}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Previous
                </button>
              )}
              
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={goToNextStep}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  Next
                  <ArrowRightIcon className="h-4 w-4 ml-2" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      <DuplicateWarningModal
        isOpen={!!duplicateModal}
        type={duplicateModal?.type}
        data={duplicateModal?.data}
        mode={duplicateModal?.mode}
        onClose={() => {
          duplicateModal?.onResolve?.(false);
          setDuplicateModal(null);
        }}
        onConfirm={() => {
          duplicateModal?.onResolve?.(true);
          setDuplicateModal(null);
        }}
        onViewClaimant={(id) => navigate(`/claimants?claimantId=${id}`)}
        onViewClaim={viewClaim}
      />

      <AdjusterConfirmModal
        isOpen={!!pendingClaim}
        claim={pendingClaim}
        onSave={saveAssignment}
        onSkip={skipPrompt}
        saving={savingRole}
      />
    </div>
  );
};

export default NewTransportationJob;
